import { Injectable } from '@angular/core';
import { IPrintData } from '../services/print.service';
import { saveAs } from 'file-saver';
import { IProcessResult } from '../entities/view-model';

const ExcelJS = require('exceljs');

@Injectable({
  providedIn: 'root'
})
export class ExcelService {
  messageText = '';

  constructor() { }

  async generateExcelFromPrintData(printDate: IPrintData, documentType: string): Promise<IProcessResult> {

    const setTotalFormula = function(addColumnTotal: boolean, colIndex: number, rowsCount: number): string {
      if (addColumnTotal) {
        const colLetter = columnToLetter(colIndex);
        return `=SUM(${colLetter}2:${colLetter}${rowsCount})`;
      }
      return '';
    };

    const setTotalfunction = function(addColumnTotal: boolean): string {
      if (addColumnTotal) {
        return `custom`;
      }
      return 'none';
    };

    const columnToLetter = function(column): string {
        var temp, letter = '';
        while (column > 0) {
          temp = (column - 1) % 26;
          letter = String.fromCharCode(temp + 65) + letter;
          column = (column - temp - 1) / 26;
        }
        return letter;
    }

    var message = '';
    var status: number = 0;

    let worksheets: IWorksheetModel[] = [];

    printDate.sections.forEach(s => {

      let simpleTable: IDataModel = undefined;
      let styleTable: ITableModel = undefined;
      if (documentType == 'csv') {
        simpleTable = {
          columns: s.columnTitles[0].map(c => ({ key: c.columnName, header: c.columnName, width: 100 })),
          rows: s.rows
        };
      } else {
        styleTable = {
          name: s.sectionTitle.replace(/\s/g, ''),
          startCell: 'A1',
          headerRow: true,
          totalsRow: true,
          style: {
            theme: 'TableStyleMedium15',
            showRowStripes: true
          },
          columns: s.columnTitles[0].map((c, i) => ({ 
            name: c.columnName, 
            filterButton: true, 
            totalsRowFormula: setTotalFormula(c.addColumnTotal, i+1, s.rows.length+1), 
            totalsRowFunction: setTotalfunction(c.addColumnTotal), 
            style: { numFmt: c.style && c.style.numberFormat ? c.style.numberFormat : ''}
          })),
          rows: s.rows,
          maximumWidthBeforeWrap: 200
        };
      }

      worksheets.push({
        sheetName: s.sectionTitle,
        columnsToFreeze: documentType == 'csv' ? 0 : printDate.rowsToFreeze ?? 0,
        rowsToFreeze: documentType == 'csv' ? 0 : printDate.columnsToFreeze ?? 0,
        simpleTable: simpleTable,
        styleTable: styleTable
      });
    });

    const ret: IExcelModel = {
      fileName: printDate.pageTitle,
      documentType: documentType,
      worksheets: worksheets
    };

    this.generateExcelImpl(JSON.stringify(ret));

    return { status: status, message: message};
  }

  /**
   * 
   * @param printDate 
   */
  async generateExcelImpl(json: string) {

    const data: IExcelModel = JSON.parse(json);

    const getLen = function(data: any): number {
      switch(Object.prototype.toString.call(data)) {
        case '[object Date]': 
        case '[object Number]': 
        case '[object Boolean]':
          return data.toString().length;
      }
      return data.length;
    };

    const validateString = function(data: string, def: string): string {
      if (!data|| data.length === 0) {
        return def;
      }
      return data; 
    };

    const validateNumber = function(data: number, def: number): number {
      if (!data) {
        return def;
      }
      return data;
    };
    const validateBool = function(data: boolean, def: boolean): boolean {
      if (!data) {
        return def;
      }
      return data;
    };

    const workbook = new ExcelJS.Workbook();
    workbook.created = new Date();

    data.worksheets.forEach(ws => {
        const workSheet = workbook.addWorksheet(ws.sheetName);

        // set styles
        if (validateNumber(ws.columnsToFreeze, 0) > 0 || validateNumber(ws.rowsToFreeze, 0) > 0) {
          workSheet.views = [
            {state: 'frozen', xSplit: ws.rowsToFreeze, ySplit: ws.columnsToFreeze}
          ];
        }

        if (ws.simpleTable) {

          workSheet.columns = ws.simpleTable.columns;

          ws.simpleTable.rows.forEach(r => {
            workSheet.addRow(r);
          });

        } else if (ws.styleTable) {
          // add a table to a sheet
          workSheet.addTable({
            name: ws.styleTable.name,
            displayName: validateString(ws.styleTable.displayName, ws.styleTable.name),
            ref: validateString(ws.styleTable.startCell, 'A1'),
            headerRow: validateBool(ws.styleTable.headerRow, true),
            totalsRow: validateBool(ws.styleTable.totalsRow, false),
            style: {
              theme: validateString(ws.styleTable.style.theme, 'TableStyleMedium1'),
              showFirstColumn: validateBool(ws.styleTable.style.showFirstColumn, false),
              showLastColumn: validateBool(ws.styleTable.style.showLastColumn, false),
              showRowStripes: validateBool(ws.styleTable.style.showRowStripes, false),
              showColumnStripes: validateBool(ws.styleTable.style.showColumnStripes, false)
            },
            columns: ws.styleTable.columns.map(c => { 
              const ret = {
                name: c.name, 
                filterButton: c.filterButton, 
                totalsRowFormula: c.totalsRowFormula, 
                totalsRowFunction: c.totalsRowFunction, 
                totalsRowLabel: c.totalsRowLabel
              };
              return ret;
            }),
            rows: ws.styleTable.rows,
          });

          for(let i = 0; i < ws.styleTable.columns.length; i++) {
            const colMaxLen = Math.max(...(ws.styleTable.columns.map(el => el.name.length)));
            const rowMaxLen = Math.max(...(ws.styleTable.rows.map(el => getLen(el[i]))));
            const cmpMaxLen = colMaxLen > rowMaxLen ? colMaxLen : rowMaxLen;
            if (ws.styleTable.maximumWidthBeforeWrap && cmpMaxLen > ws.styleTable.maximumWidthBeforeWrap) {
              workSheet.getColumn(i+1).width = ws.styleTable.maximumWidthBeforeWrap + 5;
              workSheet.getColumn(i+1).alignment = { wrapText: true };
            }
            else {
              workSheet.getColumn(i+1).width = Math.max(...(ws.styleTable.rows.map(el => getLen(el[i])))) > ws.styleTable.columns[i]?.name.length
                  ? Math.max(...(ws.styleTable.rows.map(el => getLen(el[i])))) + 5
                  : ws.styleTable.columns[i]?.name.length + 5;
            }
            workSheet.getColumn(i+1).numFmt = ws.styleTable.columns[i].style?.numFmt ?? '';
            
          }
        }
      });

    if (data.documentType && data.documentType === 'xlsx') {

      saveFile(data.fileName, workbook);
      async function saveFile(fileName, workbook) {
        const xls64 = await workbook.xlsx.writeBuffer( { base64: true } );
        saveAs(
          new Blob([xls64], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
          fileName
        );
      }
    } else {

      saveFile(data.fileName, workbook);
      async function saveFile(fileName, workbook) {
        const xls64 = await workbook.csv.writeBuffer( { base64: true } );
        saveAs(
          new Blob([xls64], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
          fileName
        );
      }
    }
  }

  /**
   *
   * @param files file names
   * @param validationRuleJson json with validation rules
   */
  async readAndValidateExcelFile(file: File): Promise<IWorkbookData> {
    try {

      const contentBuffer = await this.readFileAsync(file);
      return await this.loadExcelFileAsync(contentBuffer);

    } catch (err) {
      console.log(err);
      return { sheets: [], errorText: `Error in readAndValidateExcelFile: ${err}` };
    }
  }

  async readFileAsync(file: File) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        resolve(reader.result);
      };

      reader.onerror = reject;

      reader.readAsArrayBuffer(file);
    });
  }

  async loadExcelFileAsync(contentBuffer: any): Promise<IWorkbookData> {
    const workbookData: IWorkbookData = { sheets: [], errorText: '' };
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(contentBuffer).then(wb => {
      wb.eachSheet((sh, id) => {

        const rowArr: IRowData[] = [];
        const headerArr: string[] = [];

        for (let i = 1; i <= sh.actualRowCount; i++) {
          const row: IRowData = { cells: [] };
          for (let j = 1; j <= sh.actualColumnCount; j++) {
            const data = sh.getRow(i).getCell(j).toString();
            if (i === 1) {
              headerArr.push(data.toUpperCase());
            } else {
              row.cells.push( { value: data } );
            }
          }
          if (i > 1) {
            rowArr.push(row);
          }
        }
        workbookData.sheets.push( {name: sh.name, header: headerArr, rows: rowArr } );
      });
    });
    return workbookData;
  }
}

export interface IExcelModel {
  fileName: string;
  documentType?: string;
  worksheets: IWorksheetModel[];
}

export interface IWorksheetModel {
  sheetName?: string;
  simpleTable?: IDataModel;
  styleTable?: ITableModel;

  columnsToFreeze?: number;
  rowsToFreeze?: number;
  addFilter?: boolean;
}

export interface IDataModel {
  columns: IColumn[];
  rows: any[][];
}

export interface IColumn {
  header: string;
  key?: string;
  width?: number;
  style?: IColumnStyle;
}

export interface ITableModel {
  name: string;
  displayName?: string;
  // Top left cell of the table
  startCell?: string;
  // Show headers at top of table
  headerRow?: boolean;
  // Show totals at bottom of table
  totalsRow?: boolean;
  // Extra style properties
  style?: ITableStyle;

  columns: ITableColumn[];
  rows: any[][];

  maximumWidthBeforeWrap?: number;
}

export interface ITableStyle {
  // The colour theme of the table
  theme?: string;
  // Highlight the first column (bold)
  showFirstColumn?: boolean;
  // Highlight the last column (bold)
  showLastColumn?: boolean;
  // Alternate rows shown with background colour
  showRowStripes?: boolean;
  // Alternate rows shown with background colour
  showColumnStripes?: boolean;
}

interface IColumnStyle {
  numFmt?: string;
  font?: IFont;
  alignment ?: IAlignment;
  // thin, dotted, dashDot, hair, dashDotDot, slantDashDot, mediumDashed
  // mediumDashDotDot, mediumDashDot, medium, double, thick
  border?: string;
  fill?: IFill;
}

interface IFont {
  // 'Arial', 'Calibri', etc.
  name?: string;
  // 1 - Serif, 2 - Sans Serif, 3 - Mono, Others - unknown
  family?: string;
  // 'minor', 'major', 'none'
  scheme?: string;
  // Font charset. An integer value.
  charset?: number;
  // argb: 'FFFF0000'
  color?: IColor;
  bold?: boolean;
  italic?: boolean;
  // true, false, 'none', 'single', 'double', 'singleAccounting', 'doubleAccounting'
  underline?: string;
  strike?: boolean;
  outline?: boolean;
}

interface IColor {
  argb: string;
}

interface IAlignment {
  // left, center, right, fill, justify, centerContinuous and distributed
  horizontal?: string;
  // top, middle, bottom, distributed and justify
  vertical?: string;
  wrapText?: boolean;
  indent?: number;
  // rtl or ltr
  readingOrder?: string;
  // 0 to 90, -1 to -90 or vertical
  textRotation?: string;
}

interface IFill {
  // pattern or gradient
  type: string;
  // none, solid, darkVertical, darkGray, mediumGray, lightGray, gray125, gray0625, darkHorizontal
  // darkVertical, darkDown, darkUp, darkGrid, darkTrellis, lightHorizontal, lightVertical, lightDown
  // lightUp, lightGrid, lightTrellis, lightGrid
  pattern: string;
  // Specifies the pattern foreground color. Default is black.
  fgColor?: string;
 //  Specifies the pattern background color. Default is white.
  bgColor?: string;
}

export interface ITableColumn {
  name: string;
  totalsRowLabel?: string;
  filterButton?: boolean;
  // Totals Functions: average, countNums, count, max, min, stdDev, var, sum, custom (Requires an associated totalsRowFormula value.)
  totalsRowFunction?: string;
  totalsRowFormula?: string;
  style?: IColumnStyle;
}

/**
 *
 */
export interface IWorkbookData {
  sheets: ISheetData[];
  errorText: string;
}

/**
 *
 */
export interface ISheetData {
  name: string;
  header: string[];
  rows: IRowData[];
}

/**
 *
 */
export interface IRowData {
  cells: ICellData[];
}

/**
 *
 */
export interface ICellData {
  value: any;
}
